import React, { useEffect, useState } from 'react';
import { Spin, Table, Input, Button } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import {
  getUndisbursedLoansAction,
  updateUndisbursedLoanAction,
  updatePrincipalAmountAction,
  updateUnapprovedLoanAction,
} from '../../Loan/store/loanActions';
import { type Loan } from '../../Customer/store/customerTypes';

export default function CashDisbursementsIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const { undisbursedLoans }: any = useSelector<any>((state) => state?.loan);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [onlineValue, setOnlineValue] = useState<string>('');
  const [offlineValue, setOfflineValue] = useState<string>('');
  const [onlineDocValue, setOnlineDocValue] = useState<string>('');
  const [offlineDocValue, setOfflineDocValue] = useState<string>('');
  const [onlineGstValue, setOnlineGstValue] = useState<string>('');
  const [offlineGstValue, setOfflineGstValue] = useState<string>('');
  const [processing, setProcessing] = useState(false);

  const getLoans = async (): Promise<void> => {
    await getUndisbursedLoansAction(dispatch, {});
    setProcessing(false);
    setEditingId(null);
    setOnlineValue('');
    setOfflineValue('');
    setOnlineDocValue('');
    setOfflineDocValue('');
    setOnlineGstValue('');
    setOfflineGstValue('');
  };

  useEffect(() => {
    void getLoans();
  }, []);

  const handleEdit = (record: Loan) => {
    setEditingId(record._id);
    setOnlineValue(String(record.onlinePrincipal ?? ''));
    setOfflineValue(String(record.offlinePrincipal ?? ''));
    setOnlineDocValue(String(record.onlineDocumentCharge ?? ''));
    setOfflineDocValue(String(record.offlineDocumentCharge ?? ''));
    setOnlineGstValue(String(record.onlineGstOnDocumentCharge ?? ''));
    setOfflineGstValue(String(record.offlineGstOnDocumentCharge ?? ''));
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setOnlineValue('');
    setOfflineValue('');
    setOnlineDocValue('');
    setOfflineDocValue('');
    setOnlineGstValue('');
    setOfflineGstValue('');
  };

  const isDocChargeSplitValid = (loan: Loan) =>
    Math.abs(
      Number(onlineDocValue) + Number(offlineDocValue) - loan.documentCharge
    ) <= 0.009;

  // split is editable but must add up to the fixed GST (18%) total
  const isGstSplitValid = (loan: Loan) =>
    Math.abs(
      Number(onlineGstValue) +
        Number(offlineGstValue) -
        Number(loan.gstOnDocumentCharge ?? 0)
    ) <= 0.009;

  const handleSave = async (loan: Loan) => {
    const online = Number(onlineValue);
    const offline = Number(offlineValue);

    if (online + offline !== loan.principal) {
      alert('Online Principal + Offline Principal must match Principal amount');
      setProcessing(false);
      return;
    }

    if (!isDocChargeSplitValid(loan)) {
      alert(
        'Online Document Charge + Offline Document Charge must match Document Charge amount'
      );
      setProcessing(false);
      return;
    }

    if (!isGstSplitValid(loan)) {
      alert('Online GST + Offline GST must match GST (18%) amount');
      setProcessing(false);
      return;
    }

    setProcessing(true);
    await updatePrincipalAmountAction(dispatch, {
      id: loan._id,
      onlinePrincipal: online,
      offlinePrincipal: offline,
      onlineDocumentCharge: Number(onlineDocValue),
      offlineDocumentCharge: Number(offlineDocValue),
      onlineGstOnDocumentCharge: Number(onlineGstValue),
      offlineGstOnDocumentCharge: Number(offlineGstValue),
    });
    await getLoans();
  };

  const handleUpdate = async (loan: Loan) => {
    setProcessing(true);
    await updateUndisbursedLoanAction(dispatch, { id: loan._id });
    await getLoans();
  };

  const handleReject = async (loan: Loan) => {
    setProcessing(true);
    await updateUnapprovedLoanAction(dispatch, { id: loan._id, status: 'reject' });
    await getLoans();
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: Loan, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Loan Number',
      dataIndex: 'loanNumber',
      key: 'loanNumber',
      render: (_: any, record: Loan) => <p>{record.loanNumber}</p>,
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: (_: any, record: Loan) => (
        <img src={record.customer?.photo} style={{ height: 130, width: 130 }} />
      ),
    },
    {
      title: 'Principal',
      dataIndex: 'principal',
      key: 'principal',
      render: (_: any, record: Loan) => <p>{record.principal}</p>,
    },
    {
      title: 'Online Principal',
      dataIndex: 'onlinePrincipal',
      key: 'onlinePrincipal',
      render: (_: any, record: Loan) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={onlineValue}
            onChange={(e) => {
              setOnlineValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.onlinePrincipal}</p>
        ),
    },
    {
      title: 'Offline Principal',
      dataIndex: 'offlinePrincipal',
      key: 'offlinePrincipal',
      render: (_: any, record: Loan) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={offlineValue}
            onChange={(e) => {
              setOfflineValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.offlinePrincipal}</p>
        ),
    },
    {
      title: 'Customer charges',
      dataIndex: 'documentCharge',
      key: 'documentCharge',
      render: (_: any, record: Loan) => <p>{record.documentCharge}</p>,
    },
    {
      title: 'GST (18%)',
      dataIndex: 'gstOnDocumentCharge',
      key: 'gstOnDocumentCharge',
      render: (_: any, record: Loan) => <p>{record.gstOnDocumentCharge}</p>,
    },
    {
      title: 'Online Doc Charge',
      dataIndex: 'onlineDocumentCharge',
      key: 'onlineDocumentCharge',
      render: (_: any, record: Loan) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={onlineDocValue}
            onChange={(e) => {
              setOnlineDocValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.onlineDocumentCharge}</p>
        ),
    },
    {
      title: 'Offline Doc Charge',
      dataIndex: 'offlineDocumentCharge',
      key: 'offlineDocumentCharge',
      render: (_: any, record: Loan) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={offlineDocValue}
            onChange={(e) => {
              setOfflineDocValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.offlineDocumentCharge}</p>
        ),
    },
    {
      title: 'Online GST',
      dataIndex: 'onlineGstOnDocumentCharge',
      key: 'onlineGstOnDocumentCharge',
      render: (_: any, record: Loan) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={onlineGstValue}
            onChange={(e) => {
              setOnlineGstValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.onlineGstOnDocumentCharge}</p>
        ),
    },
    {
      title: 'Offline GST',
      dataIndex: 'offlineGstOnDocumentCharge',
      key: 'offlineGstOnDocumentCharge',
      render: (_: any, record: Loan) =>
        editingId === record._id ? (
          <Input
            type="number"
            value={offlineGstValue}
            onChange={(e) => {
              setOfflineGstValue(e.target.value);
            }}
            style={{ width: 100 }}
            size="small"
          />
        ) : (
          <p style={{ margin: 0 }}>{record.offlineGstOnDocumentCharge}</p>
        ),
    },
    {
      title: 'Edit',
      dataIndex: 'edit',
      key: 'edit',
      width: 120,
      render: (_: any, record: Loan) =>
        editingId === record._id ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <Button
              icon={<SaveOutlined style={{ color: '#000' }} />}
              size="small"
              type="link"
              loading={processing}
              onClick={() => {
                void handleSave(record);
              }}
              disabled={
                Number(onlineValue) + Number(offlineValue) !==
                  record.principal ||
                !isDocChargeSplitValid(record) ||
                !isGstSplitValid(record)
              }
            ></Button>
            <Button
              icon={<CloseOutlined style={{ color: '#000' }} />}
              size="small"
              type="link"
              disabled={processing}
              onClick={handleCancelEdit}
            ></Button>
          </div>
        ) : (
          <Button
            icon={<EditOutlined style={{ color: '#000' }} />}
            size="small"
            type="link"
            onClick={() => {
              handleEdit(record);
            }}
          ></Button>
        ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: 300,
      render: (_: any, record: Loan) => {
        const disburseDisabled = processing || editingId === record._id;
        return (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="search__button"
            style={{
              background: !disburseDisabled ? '#27ae60' : '#b2b2b2',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 24px',
              fontWeight: 700,
              cursor: !disburseDisabled ? 'pointer' : 'not-allowed',
              opacity: !disburseDisabled ? 1 : 0.7,
            }}
            disabled={disburseDisabled}
            onClick={() => {
              if (!disburseDisabled) {
                setProcessing(true);
                void handleUpdate(record);
              }
            }}
          >
            Cash Disbursed
          </button>
          <button
            className="search__button"
            style={{
              background: !processing ? '#e74c3c' : '#b2b2b2',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 24px',
              fontWeight: 700,
              cursor: !processing ? 'pointer' : 'not-allowed',
              opacity: !processing ? 1 : 0.7,
            }}
            disabled={processing}
            onClick={async () => {
              if (!processing) {
                setProcessing(true);
                void handleReject(record);
              }
            }}
          >
            Reject
          </button>
        </div>
        );
      },
    },
  ];

  return (
    <div className="users__table__page">
      {!(undisbursedLoans.length > 0) && <Spin spinning={false} fullscreen />}
      <div style={{ width: '100%' }}>
        <Table dataSource={undisbursedLoans} columns={columns} />
      </div>
    </div>
  );
}
